import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import PropdocsModal from '../../../../../modal/PropdocsModal';
import { cancelDocumentApprovalRequest } from '../../../../../../api/mutations';
import { getDocumentById } from '../../../../../../api/queries_v2';
import { useNotifications } from '../../../../../../notifications';
import { fillPlaceholders, paths } from '../../../../../../routing';
import { OptionalMessageInputRef } from '../../../../document-wizard-modal/custom-modals/document-wizard-action-modal/OptionalMessageInputRef';
import { cancelApprovalRequestSuccessMessage } from './RequestInternalApprovalModalConstants';

interface Props {
  onHide: () => void;
  show: boolean;
  documentId: string;
  dealId: string;
  teamId: string;
  requestId: string;
}

const CancelApprovalRequestModal = ({ onHide, show, documentId, dealId, requestId, teamId }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const additionalMessageInputRef = useRef<HTMLTextAreaElement>(null);
  const dealDeatilsUrl = fillPlaceholders(paths.deals.detail, {
    teamId,
    dealId,
  });
  const [cancelDocumentApprovalRequestMutation] = useMutation(cancelDocumentApprovalRequest, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: {
          documentId,
        },
      },
    ],
  });

  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const handleCancelApprovalRequest = useCallback(async () => {
    setIsLoading(true);
    const additionalMessage = additionalMessageInputRef.current?.value;

    try {
      await cancelDocumentApprovalRequestMutation({
        variables: {
          requestId,
          additionalMessage,
        },
      });

      // Close modal first to avoid render conflicts
      onHide();

      // Navigate to deal details
      navigate(dealDeatilsUrl);

      // Show success notification with slight delay to avoid render conflicts
      setTimeout(() => {
        notifySuccess({
          message: formatMessage(cancelApprovalRequestSuccessMessage),
        });
      }, 100);
    } catch (error) {
      notifyError({ message: error });
    } finally {
      setIsLoading(false);
    }
  }, [
    cancelDocumentApprovalRequestMutation,
    notifyError,
    requestId,
    dealDeatilsUrl,
    notifySuccess,
    formatMessage,
    navigate,
    onHide,
  ]);

  return (
    <PropdocsModal
      size="lg"
      show={show}
      setShow={onHide}
      title={
        <FormattedMessage
          id="document-wizard-modal.handle_internal_approval"
          defaultMessage="Cancel Approval Request"
        />
      }
    >
      <>
        <div style={{ marginBottom: '1.5rem' }}>Canceling will remove all received approvals</div>
        <OptionalMessageInputRef ref={additionalMessageInputRef} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
          <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancelApprovalRequest}
            disabled={isLoading}
          >
            {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
            Confirm
          </button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default CancelApprovalRequestModal;
